/**
 * Values the database works out, kept on screen.
 *
 * `fromDb(spec, run, initial)` is a computed whose answer comes back from a
 * query. `spec()` is synchronous and reactive — it reads the filters, the
 * window, whatever the question depends on — and says what to ask; `run(spec)`
 * asks it. The answer lands in a ref, so templates and other computeds read it
 * like any other: `.value`, reactive, never a promise.
 *
 * Every one of them also depends on `state.dataVersion`, which each write bumps
 * (`dataChanged()`), and on `state.ready`: nothing is asked before the database
 * is open and loaded. A spec that returns null asks nothing. An answer to a
 * question that has since been replaced is thrown away, so a fast typist never
 * sees an older search overwrite a newer one.
 *
 * `settled()` resolves once nothing is in flight — for the tests, which change
 * the state and then want the numbers it leads to.
 */
import { shallowRef, watch } from 'vue'
import { state } from './state'

let pending = 0
let waiting = []

/** Resolves when no answer is outstanding — including ones the last answer set off. */
function check() {
  // A macrotask later: an answer that lands schedules its dependents'
  // questions as microtasks, and they must have started before "idle" is true.
  setTimeout(() => {
    if (pending) return
    const ready = waiting
    waiting = []
    ready.forEach((resolve) => resolve())
  }, 0)
}

export function settled() {
  return new Promise((resolve) => {
    waiting.push(resolve)
    check()
  })
}

/** Something was written: every question is asked again. */
export function dataChanged() {
  state.dataVersion++
}

export function fromDb(spec, run, initial) {
  const out = shallowRef(initial)
  let ticket = 0
  let lastKey
  watch(
    () => (state.ready ? [state.dataVersion, spec()] : null),
    async (next) => {
      if (!next || next[1] == null) return
      // Recomputed but asking the same thing: nothing to do.
      const key = JSON.stringify(next)
      if (key === lastKey) return
      lastKey = key
      const mine = ++ticket
      pending++
      try {
        const value = await run(next[1])
        if (mine === ticket) out.value = value
      } catch (err) {
        // The question can be asked again; keeping the last answer beats a blank.
        lastKey = undefined
        if (err?.code !== 'disconnected') console.error('OKANE could not work that out', err)
      } finally {
        pending--
        if (!pending) check()
      }
    },
    { immediate: true },
  )
  return out
}

/** For work outside `fromDb` that tests must also wait for (the list's pages). */
export async function tracked(promise) {
  pending++
  try {
    return await promise
  } finally {
    pending--
    if (!pending) check()
  }
}
